import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { makeSelectEnvVars } from 'containers/Auth_old/selectors';
import {
  PusherEventHandlers,
  subscribeToPusherChannel,
} from 'utils/pusherClient';

/**
 * Binds `handlers` to `channelName` on the shared Pusher socket for as long
 * as the component is mounted and `channelName` is set. Handlers are read
 * through a ref, so a new closure on every render does not resubscribe.
 * Pass `null` as the channel to hold no subscription (and no socket).
 */
export default function usePusherChannel(
  channelName: string | null | undefined,
  handlers: PusherEventHandlers,
) {
  const envVars = useSelector(makeSelectEnvVars());
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const appKey = envVars?.REACT_APP_PUSHER_APP_KEY;
  const cluster = envVars?.REACT_APP_PUSHER_CLUSTER;

  useEffect(() => {
    if (!channelName || !appKey) return undefined;

    const events = Object.keys(handlersRef.current);
    const forwarders: PusherEventHandlers = {};
    events.forEach(event => {
      forwarders[event] = (data: never) => {
        const handler = handlersRef.current[event];
        if (handler) handler(data);
      };
    });

    return subscribeToPusherChannel(
      { REACT_APP_PUSHER_APP_KEY: appKey, REACT_APP_PUSHER_CLUSTER: cluster },
      channelName,
      forwarders,
    );
  }, [channelName, appKey, cluster]);
}
